import { useState } from "react";
import { useTranslation } from "react-i18next";
import { httpsCallable } from "firebase/functions";
import { functions } from "../services/firebase";
import Card from "react-bootstrap/esm/Card";
import Button from "react-bootstrap/esm/Button";
import Alert from "react-bootstrap/esm/Alert";

/**
 * Calendar subscription: the preacher's services as a feed their calendar app
 * keeps up to date. The feed has its own link – it only shows the services and
 * cannot be used to change anything.
 */
export default function MyPlanCalendar({ token }: { token: string }) {
    const { t } = useTranslation();
    const [url, setUrl] = useState("");
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState<{ variant: string; text: string } | null>(null);

    const fetchUrl = async (renew = false): Promise<string> => {
        const call = httpsCallable<{ token: string; renew: boolean }, { url: string }>(functions, "getMyCalendarLink");
        const result = (await call({ token, renew })).data.url;
        setUrl(result);
        return result;
    };

    const act = async (action: (link: string) => Promise<void> | void, renew = false) => {
        setBusy(true);
        setNotice(null);
        try {
            await action(renew || !url ? await fetchUrl(renew) : url);
        } catch {
            setNotice({ variant: "danger", text: t("myPlan.calendarFailed") });
        } finally {
            setBusy(false);
        }
    };

    const subscribe = () =>
        act((link) => {
            // webcal:// makes the device offer its calendar app for a subscription.
            window.location.href = link.replace(/^https?:/, "webcal:");
        });

    const copy = () =>
        act(async (link) => {
            await navigator.clipboard.writeText(link);
            setNotice({ variant: "success", text: t("myPlan.calendarCopied") });
        });

    const renew = () => act(() => setNotice({ variant: "success", text: t("myPlan.calendarRenewed") }), true);

    return (
        <Card className="mb-3">
            <Card.Body>
                <h5>{t("myPlan.calendarHeading")}</h5>
                <p className="text-muted small">{t("myPlan.calendarIntro")}</p>
                <div className="d-flex gap-2 flex-wrap">
                    <Button variant="outline-primary" disabled={busy} onClick={subscribe}>
                        {t("myPlan.calendarSubscribe")}
                    </Button>
                    <Button variant="outline-secondary" disabled={busy} onClick={copy}>
                        {t("myPlan.calendarCopy")}
                    </Button>
                </div>
                {url && (
                    <div className="mt-3">
                        <code className="d-block small text-break">{url}</code>
                        <Button variant="link" size="sm" className="px-0" disabled={busy} onClick={renew}>
                            {t("myPlan.calendarRenew")}
                        </Button>
                    </div>
                )}
                {notice && <Alert variant={notice.variant} className="mt-3 mb-0">{notice.text}</Alert>}
            </Card.Body>
        </Card>
    );
}
